import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Cliente } from "../../../services/Cliente";
import { useContextSite } from "../../../context/Context";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { IClienteDTO } from "../../../types/cliente";
import { TipoClienteEnum } from "../../../enums/tipoCliente";
import { ISelectOptions } from "../../../types/inputs";
import { Ibge } from "../../../services/Ibge";
import { ViaCep } from "../../../services/ViaCep";
import { maskCep, maskCpf } from "../../../utils/masks";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const TipoOptions = Object.values(TipoClienteEnum).map((i) => ({
  value: i,
  label: i,
}));

const schemaEndereco = z.object({
  bairro: z.string().min(2, "Campo obrigatorio"),
  cep: z.string().min(9, "Campo obrigatorio"),
  cidade: z.string().min(2, "Campo obrigatorio"),
  complemento: z.string().optional().or(z.literal("")),
  logradouro: z.string().min(2, "Campo obrigatorio"),
  numero: z.string().min(1, "Campo obrigatorio"),
  uf: z.string().min(1, "Campo obrigatorio"),
});

const schema = z.object({
  email: z.string().email().optional().or(z.literal("")),
  nome: z.string().min(1),
  tipo: z.string().min(1),
  telefone: z.string().min(1),
  cpfCnpj: z.string().min(2, "Campo obrigatorio"),
  endereco: schemaEndereco,
});

export const useClientEdit = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const { setIsLoad } = useContextSite();
  const [ufOptions, setUfOptions] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const [cidadeTemp, setCidadeTemp] = useState("");
  const [cidades, setCidades] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );

  function handleCep() {
    if (watch("endereco.cep").length === 9) {
      setIsLoad(true);
      setTimeout(() => {
        ViaCep.get(watch("endereco.cep"))
          .then(({ data }) => {
            setValue("endereco.logradouro", data.logradouro);
            setValue("endereco.bairro", data.bairro);
            // setValue("endereco.cidade", data.localidade);
            setValue("endereco.uf", data.uf);
            setCidadeTemp(data.localidade);
          })
          .catch((erro) => toast.error("Cep não encontrado"))
          .finally(() => setIsLoad(false));
      }, 1000);
    }
  }

  const { register, control, setValue, watch, handleSubmit } =
    useForm<IClienteDTO>({
      defaultValues: {
        email: "",
        nome: "",
        cpfCnpj: "",
        tipo: "" as TipoClienteEnum,
        telefone: "",
        endereco: {
          bairro: "",
          cep: "",
          cidade: "",
          complemento: "",
          logradouro: "",
          numero: "",
          uf: "",
        },
      },

      resolver: zodResolver(schema),
    });

  const getClient = useCallback(() => {
    setIsLoad(true);
    Cliente.getById({ id })
      .then(({ data }) => {
        Object.entries(data).forEach(([key, value]) => {
          setValue(key as keyof IClienteDTO, value || "");

          if (key === "endereco") {
            Object.entries(data.endereco).forEach(([k, v]) => {
              setValue(`endereco.${k}` as keyof IClienteDTO, v || "");
            });
          }
        });

        setCidadeTemp(data.endereco.cidade);
        setValue("endereco.cep", maskCep(data.endereco.cep));
        setValue("cpfCnpj", maskCpf(data.cpfCnpj));
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      )
      .finally(() => {
        setIsLoad(false);
      });
  }, []);

  useEffect(() => {
    getOptions();

    if (id) {
      getClient();
    }
  }, [id]);

  const getOptions = useCallback(() => {
    Ibge.UFs()
      .then(({ data }) => {
        const options = data.map((i) => ({
          value: i.sigla,
          label: i.sigla,
          element: i,
        }));

        setUfOptions(options);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      );
  }, []);

  useEffect(() => {
    if (watch("endereco.uf")) {
      setValue("endereco.cidade", "");
      Ibge.CidadesPorEstado({ sigla: watch("endereco.uf") })
        .then(({ data }) => {
          const options = data.map((item) => ({
            value: item.nome,
            label: item.nome,
            element: item,
          }));
          setCidades(options);
          setValue("endereco.cidade", cidadeTemp);
        })
        .then(() => {
          setCidadeTemp("");
        })
        .catch((erro) => toast.error("Erro ao requisitar a lista de cidades"));
    }
  }, [watch("endereco.uf")]);

  useEffect(() => {
    setValue("endereco.cep", maskCep(watch("endereco.cep")));
  }, [watch("endereco.cep")]);

  return {
    Controller,
    register,
    control,
    TipoOptions,
    ufOptions,
    watch,
    cidades,
    handleCep,
    handleSubmit,
  };
};
